import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Plus, Search, Upload, X, ChevronDown, MessageSquare, Trash2, Edit3, Phone, Mail, ExternalLink } from "lucide-react";

const STATUSES = ["all", "new", "contacted", "qualified", "visit_scheduled", "converted", "dormant", "lost"];
const SOURCES = ["manual", "website", "referral", "social_media", "csv_import", "other"];

export default function LeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [showAiMsg, setShowAiMsg] = useState(null);
  const [aiMessage, setAiMessage] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const fileRef = useRef(null);

  const fetchLeads = async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      const { data } = await api.get("/leads", { params });
      setLeads(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, [statusFilter, searchQuery]);

  const handleCreate = async (formData) => {
    try {
      await api.post("/leads", formData);
      setShowCreate(false);
      fetchLeads();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      await api.put(`/leads/${id}`, formData);
      setEditLead(null);
      fetchLeads();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      fetchLeads();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/leads/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      alert(`Imported ${data.imported} leads`);
      setShowImport(false);
      fetchLeads();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const generateMessage = async (lead, type) => {
    setShowAiMsg(lead.id);
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/generate-message", {
        context_type: type,
        lead_name: lead.name,
        additional_context: lead.notes || ""
      });
      setAiMessage(data.message);
    } catch {
      setAiMessage("Failed to generate message.");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="leads-page">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#868E96]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-9 pr-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm w-60"
              placeholder="Search leads..."
              data-testid="leads-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm bg-white"
            data-testid="leads-status-filter"
          >
            {STATUSES.map(s => <option key={s} value={s}>{s === "all" ? "All Statuses" : s.replace("_", " ")}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="h-9 px-3 border border-border text-sm font-medium hover:bg-[#F1F3F5] transition-colors flex items-center gap-2 rounded-sm"
            data-testid="import-csv-button"
          >
            <Upload size={14} /> Import CSV
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] transition-colors flex items-center gap-2 rounded-sm"
            data-testid="create-lead-button"
          >
            <Plus size={14} /> New Lead
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-[#F8F9FA]">
              <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Name</th>
              <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Contact</th>
              <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Status</th>
              <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Source</th>
              <th className="text-left px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Budget</th>
              <th className="text-right px-4 py-2.5 text-xs tracking-[0.15em] uppercase font-bold text-[#868E96]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-[#868E96]">Loading...</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-[#868E96]">No leads found. Create one to get started.</td></tr>
            ) : (
              leads.map((lead, i) => (
                <tr key={lead.id} className="border-b border-border table-row-hover animate-fade-in" style={{ animationDelay: `${i * 0.02}s` }}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#0A0A0A] hover:text-[#002FA7] cursor-pointer transition-colors" onClick={() => navigate(`/leads/${lead.id}`)} data-testid={`lead-name-${lead.id}`}>{lead.name}</p>
                    {lead.location_pref && <p className="text-xs text-[#868E96]">{lead.location_pref}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {lead.email && <span className="flex items-center gap-1 text-xs text-[#495057]"><Mail size={11} />{lead.email}</span>}
                    </div>
                    {lead.phone && <span className="flex items-center gap-1 text-xs text-[#495057] mt-0.5"><Phone size={11} />{lead.phone}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`status-badge status-${lead.status}`}>{lead.status?.replace("_", " ")}</span>
                  </td>
                  <td className="px-4 py-3 text-[#495057]">{lead.source}</td>
                  <td className="px-4 py-3 text-[#495057]">
                    {lead.budget_max > 0 ? `$${(lead.budget_min || 0).toLocaleString()} - $${lead.budget_max.toLocaleString()}` : "-"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => generateMessage(lead, lead.status === "dormant" ? "reactivation" : "reply")} className="p-1.5 hover:bg-[#F1F3F5] rounded-sm" title="Generate message" data-testid={`lead-message-${lead.id}`}><MessageSquare size={14} className="text-[#002FA7]" /></button>
                      <button onClick={() => setEditLead(lead)} className="p-1.5 hover:bg-[#F1F3F5] rounded-sm" title="Edit" data-testid={`lead-edit-${lead.id}`}><Edit3 size={14} className="text-[#495057]" /></button>
                      <button onClick={() => handleDelete(lead.id)} className="p-1.5 hover:bg-red-50 rounded-sm" title="Delete" data-testid={`lead-delete-${lead.id}`}><Trash2 size={14} className="text-[#C92A2A]" /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {(showCreate || editLead) && (
        <LeadFormModal
          lead={editLead}
          onSave={editLead ? (data) => handleUpdate(editLead.id, data) : handleCreate}
          onClose={() => { setShowCreate(false); setEditLead(null); }}
        />
      )}

      {/* CSV Import Modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setShowImport(false)}>
          <div className="bg-white border border-border p-6 w-96" onClick={e => e.stopPropagation()} data-testid="csv-import-modal">
            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Chivo' }}>Import Leads from CSV</h3>
            <p className="text-sm text-[#868E96] mb-4">CSV should have columns: name, email, phone, notes, budget_min, budget_max, location, property_type</p>
            <input type="file" accept=".csv" ref={fileRef} onChange={handleCsvImport} className="text-sm" data-testid="csv-file-input" />
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowImport(false)} className="h-9 px-4 border border-border text-sm rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* AI Message Modal */}
      {showAiMsg && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setShowAiMsg(null)}>
          <div className="bg-white border border-border p-6 w-[28rem]" onClick={e => e.stopPropagation()} data-testid="ai-message-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Chivo' }}>Suggested Message</h3>
              <button onClick={() => setShowAiMsg(null)} className="p-1 hover:bg-[#F1F3F5] rounded-sm"><X size={16} /></button>
            </div>
            {aiLoading ? (
              <p className="text-sm text-[#868E96] py-4">Generating message...</p>
            ) : (
              <div>
                <textarea
                  value={aiMessage}
                  onChange={(e) => setAiMessage(e.target.value)}
                  className="w-full h-32 p-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm resize-none"
                  data-testid="ai-message-textarea"
                />
                <div className="flex justify-end gap-2 mt-3">
                  <button onClick={() => { navigator.clipboard.writeText(aiMessage); }} className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] rounded-sm" data-testid="copy-message-button">Copy</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LeadFormModal({ lead, onSave, onClose }) {
  const [form, setForm] = useState({
    name: lead?.name || "",
    email: lead?.email || "",
    phone: lead?.phone || "",
    source: lead?.source || "manual",
    status: lead?.status || "new",
    notes: lead?.notes || "",
    budget_min: lead?.budget_min || 0,
    budget_max: lead?.budget_max || 0,
    location_pref: lead?.location_pref || "",
    property_type_pref: lead?.property_type_pref || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white border border-border p-6 w-[32rem] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="lead-form-modal">
        <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Chivo' }}>{lead ? "Edit Lead" : "New Lead"}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Name *</label>
              <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="lead-name-input" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="lead-email-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Phone</label>
              <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="lead-phone-input" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Source</label>
              <select value={form.source} onChange={e => setForm({...form, source: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm bg-white" data-testid="lead-source-select">
                {SOURCES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Budget Min</label>
              <input type="number" value={form.budget_min} onChange={e => setForm({...form, budget_min: Number(e.target.value)})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="lead-budget-min-input" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Budget Max</label>
              <input type="number" value={form.budget_max} onChange={e => setForm({...form, budget_max: Number(e.target.value)})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="lead-budget-max-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Location Preference</label>
              <input type="text" value={form.location_pref} onChange={e => setForm({...form, location_pref: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="lead-location-input" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Property Type</label>
              <input type="text" value={form.property_type_pref} onChange={e => setForm({...form, property_type_pref: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" data-testid="lead-property-type-input" />
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full h-20 p-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm resize-none" data-testid="lead-notes-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-9 px-4 border border-border text-sm rounded-sm hover:bg-[#F1F3F5]" data-testid="lead-form-cancel">Cancel</button>
            <button type="submit" className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] rounded-sm" data-testid="lead-form-submit">{lead ? "Update" : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
